// =========================================================================
// AUTH BRIDGE — Hybrid native + REST con fallback automático.
// -------------------------------------------------------------------------
// Estrategia:
//   1. Intenta primero @react-native-firebase/auth (SDK nativo) con timeout
//      de 8 s. Si responde, perfecto — el resto de la app sigue usando
//      `nativoActual()` como siempre.
//   2. Si el SDK nativo se cuelga (Play Integrity / App Check problemático),
//      cae al cliente REST de Identity Toolkit y persiste el user en
//      `restAuthCache` para que verify-email y otras pantallas lo lean.
//
// Por qué este patrón:
//   El backend REST de Firebase responde en ms sin pasar por Play Integrity
//   (probado con curl). El SDK nativo intenta el handshake y se queda
//   mudo cuando algo en el device no entrega token. El fallback nos permite
//   tener AUTH FUNCIONAL hoy mientras se diagnostica la config nativa.
// =========================================================================

import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { getNativeAuth, auth as webAuth, usuarioActual } from './firebaseConfig';
import {
  signInWithEmailAndPassword as nativeSignIn,
  createUserWithEmailAndPassword as nativeCreate,
  sendEmailVerification as nativeSendVerification,
  signOut as nativeSignOut,
  signInWithCustomToken as nativeSignInWithCustomToken,
} from '@react-native-firebase/auth';
import { signOut as webSignOut } from 'firebase/auth';
import { authRest, type RestAuthUser } from './authRest';
import { fetchWithRetry } from './fetchWithRetry';
import { useAppStore } from '../store/useAppStore';
import { notificationService } from './notificationService';
import { rideSessionStore } from './rideSessionStore';
import { locationService } from './locationService';
import { firestoreService } from './firestoreService';

// ══════════════════════════════════════════════════════════════════════════
// CIERRE DE SESIÓN EN CURSO — H32, auditoría 16-sep-2026.
//
// `generacionSesion` sube cada vez que empieza un cierre de sesión. Todo lo que
// trabaja en segundo plano con la sesión de ANTES (el canje del custom token,
// la renovación del token REST, la lectura del perfil en `_layout.tsx`) apunta
// la generación al empezar y, si al terminar ya no coincide, NO escribe nada.
//
// Por qué hace falta: esas tareas tardan segundos y el cierre de sesión dura
// milisegundos. Sin esta marca, una respuesta que llegaba tarde volvía a meter
// la sesión que el usuario acababa de cerrar: el canje firmaba otra vez el SDK
// nativo, la renovación volvía a escribir la caché REST en disco, y el
// observador de `_layout.tsx` (que usa `user?.uid ?? bridge?.uid`) devolvía el
// uid al store y re-registraba el token de push. Para el usuario: "cerré sesión
// y seguí dentro", de forma intermitente.
// ══════════════════════════════════════════════════════════════════════════
let generacionSesion = 0;
let cerrandoSesion = false;

/** true mientras `authBridge.signOut()` está corriendo. Lo consulta `_layout.tsx`. */
export function sesionCerrandose(): boolean {
  return cerrandoSesion;
}

/** Número que cambia con cada cierre de sesión (ver la nota de arriba). */
export function generacionDeSesion(): number {
  return generacionSesion;
}

/**
 * Espera una tarea como mucho `ms` y NUNCA lanza.
 *
 * Cerrar sesión no puede quedarse colgado porque falte la red: una escritura de
 * Firestore sin conexión no resuelve hasta que el servidor responde. Pasado el
 * tope se sigue con lo demás; la pieza que no llegó queda perdida, no bloquea.
 */
async function esperarConTope(tarea: Promise<unknown>, ms: number, queEra: string): Promise<void> {
  let reloj: ReturnType<typeof setTimeout> | null = null;
  try {
    await Promise.race([
      tarea,
      new Promise<void>((resolve) => {
        reloj = setTimeout(() => {
          console.warn(`[authBridge] ${queEra}: sin respuesta en ${ms} ms, se sigue sin esperar`);
          resolve();
        }, ms);
      }),
    ]);
  } catch (e: any) {
    console.warn(`[authBridge] ${queEra} falló:`, e?.code ?? '', e?.message ?? e);
  } finally {
    if (reloj) clearTimeout(reloj);
  }
}

/**
 * APAGA AL CHOFER EN ESTE TELÉFONO — H09 / H01, auditoría 16-sep-2026.
 *
 * El vigilante de GPS es un singleton que vive fuera de React: cambiar de rol o
 * cerrar sesión no lo para. Seguía publicando la posición en /driverLocations y
 * /drivers, y /drivers/{uid} seguía con `isOnline: true`, así que los pasajeros
 * le mandaban solicitudes a un chofer que ya no estaba escuchando — y el único
 * botón para desconectarse solo se dibuja con rol de chofer.
 *
 * Es el mismo trío que usa "Desconectarse" en map.tsx y en el mismo orden:
 * primero se apaga lo que emite (GPS), después la presencia.
 *
 * Al CERRAR SESIÓN apaga de verdad (el mapa se desmonta, no queda nada
 * desincronizado). Al CAMBIAR DE MODO solo limpia restos: a un chofer con el
 * GPS vivo `prepararCambioDeModo` le pide antes que se desconecte él.
 *
 * ⚠️ La presencia solo se escribe si HAY EVIDENCIA de que estaba en línea (el
 * vigilante vivo o la intención guardada). `setDriverOnline` hace un
 * `set(..., {merge:true})`: llamarlo sin condición le CREARÍA un documento en
 * /drivers a cada pasajero que cambie de modo o cierre sesión. Y sin `uid` no se
 * escribe nada en el servidor: el borrado de cuenta (profile.tsx) vacía el store
 * ANTES de llamar a signOut justamente para que no se recree ningún documento.
 *
 * `stopTracking` sí va siempre: no hace nada si no había vigilante y, si había
 * uno a medio encender, lo mata al aterrizar.
 */
export async function apagarChoferSiEstabaEnLinea(uid: string | null): Promise<void> {
  let estabaEnLinea = false;
  try {
    estabaEnLinea = locationService.modoDeRastreoActual() !== null;
  } catch {}
  if (!estabaEnLinea) {
    try {
      estabaEnLinea = (await rideSessionStore.read()).driverOnline;
    } catch {}
  }

  await esperarConTope(locationService.stopTracking(), 8000, 'apagar el GPS');
  await esperarConTope(rideSessionStore.setDriverOnline(false), 3000, 'borrar la intención de estar en línea');

  if (estabaEnLinea && uid) {
    await esperarConTope(
      firestoreService.setDriverOnline(uid, { isOnline: false }),
      8000,
      'apagar la presencia del chofer'
    );
  }
}

const ESTADOS_DE_VIAJE_ACTIVO = new Set(['PENDING', 'ACCEPTED', 'ARRIVED', 'IN_PROGRESS']);
const ROLES_DE_CHOFER = new Set(['DRIVER', 'DELIVERY_DRIVER', 'SCHOOL_DRIVER']);

export type ResultadoCambioDeModo =
  | { ok: true }
  | { ok: false; titulo: string; mensaje: string };

/**
 * ANTES DE CAMBIAR DE MODO — H09 / H10, auditoría 16-sep-2026.
 *
 * La auditoría pedía borrar el puntero del viaje (`rideSessionStore.clearAll`)
 * en cada cambio de modo. Eso le quitaba la pantalla de su viaje a quien lo
 * tiene EN CURSO: el viaje sigue vivo en el servidor y el usuario ya no lo ve.
 * Y apagar el GPS a un chofer en pleno viaje deja al pasajero con el carro
 * congelado en el mapa. Aquí se hace la variante segura:
 *
 *  · Si el puntero apunta a un viaje que YA NO está activo (o que no es de este
 *    usuario), se borra y se sigue.
 *  · Si el viaje está vivo y el cambio lo rompería, NO se cambia y se dice por
 *    qué. Lo rompen dos casos: el chofer que se va de modo chofer (se apagaría
 *    su GPS), y el pasajero que pasa a un rol de chofer (el arranque del mapa
 *    montaría su propia solicitud como si fuera un viaje que él conduce).
 *    Pasar de pasajero a un panel de negocio no rompe nada: el puntero se
 *    conserva y el viaje vuelve al regresar a pasajero, como hasta hoy.
 *  · Si no se puede confirmar (sin red), tampoco se cambia: mejor pedir que lo
 *    intente otra vez que apagarle el GPS a alguien que va manejando.
 *
 * CHOFER EN LÍNEA (sin viaje): la auditoría pedía apagarlo aquí mismo (GPS +
 * presencia). NO se hace así, y es a propósito: el interruptor "En línea" es
 * estado LOCAL del mapa, que no se desmonta al cambiar de pestaña. Si se le
 * apagaba el GPS por detrás, al volver a modo chofer el mapa seguía diciendo
 * "En línea" sin GPS ni presencia — y si aceptaba un viaje, el pasajero veía
 * el carro congelado (`setTrackingMode` no hace nada sin vigilante vivo). Por
 * eso se le pide que se desconecte con SU botón, que deja todo coherente.
 * Se mira el vigilante vivo (`modoDeRastreoActual`) y no la intención guardada
 * en disco: esa puede quedar vieja en un rol que no tiene el botón para
 * apagarla, y el usuario quedaría sin salida.
 *
 * Al final, `apagarChoferSiEstabaEnLinea` limpia lo que haya quedado suelto
 * (intención vieja en disco, presencia que no se apagó). La oferta pendiente
 * del chofer NO se toca: si el pasajero la acepta mientras tanto, al volver a
 * modo chofer el mapa la reconoce como suya.
 */
export async function prepararCambioDeModo(
  uid: string | null,
  rolDestino: string | null
): Promise<ResultadoCambioDeModo> {
  const sesion = await rideSessionStore.read();

  if (sesion.activeRideId) {
    const esDeChofer = sesion.activeRideRole === 'DRIVER';
    const destinoEsChofer = !!rolDestino && ROLES_DE_CHOFER.has(rolDestino);
    const loRompe = esDeChofer ? rolDestino !== 'DRIVER' : destinoEsChofer;

    if (loRompe) {
      let viaje: any = null;
      let noEsSuyo = false;
      let reloj: ReturnType<typeof setTimeout> | null = null;
      try {
        viaje = await Promise.race([
          firestoreService.getRideById(sesion.activeRideId),
          new Promise((_, reject) => {
            reloj = setTimeout(() => reject(new Error('timeout')), 8000);
          }),
        ]);
      } catch (e: any) {
        // permission-denied CON sesión abierta = el viaje no es de este usuario
        // (un puntero heredado de otra cuenta). Sin sesión, ese mismo error es
        // la sesión que aún no llega, y no dice nada del viaje.
        const denegado = String(e?.code ?? '').includes('permission-denied');
        let haySesion = false;
        try { haySesion = !!usuarioActual(); } catch {}
        if (denegado && haySesion) {
          noEsSuyo = true;
        } else {
          return {
            ok: false,
            titulo: 'No pudimos confirmar tu viaje',
            mensaje: 'Revisa tu conexión e inténtalo otra vez. Antes de cambiar de modo necesitamos saber si tu viaje ya terminó.',
          };
        }
      } finally {
        // Sin esto el reloj rechazaba 8 s después sin nadie escuchando, y el
        // manejador global lo contaba como un fallo de la app.
        if (reloj) clearTimeout(reloj);
      }

      const participa =
        !!viaje && !!uid && (viaje.passengerId === uid || viaje.driverId === uid);
      const sigueActivo =
        !!viaje &&
        ESTADOS_DE_VIAJE_ACTIVO.has(String(viaje.status ?? '')) &&
        !firestoreService.isRideStale(viaje);

      if (!noEsSuyo && participa && sigueActivo) {
        if (esDeChofer) {
          return {
            ok: false,
            titulo: 'Tienes un viaje en curso',
            mensaje: 'Termina el viaje o cancélalo antes de cambiar de modo. Si cambias ahora se apaga tu ubicación y el pasajero deja de ver dónde vienes.',
          };
        }
        return {
          ok: false,
          titulo: String(viaje.status) === 'PENDING'
            ? 'Tienes una solicitud de viaje abierta'
            : 'Tienes un viaje en curso',
          mensaje: String(viaje.status) === 'PENDING'
            ? 'Cancela tu solicitud antes de pasarte a modo chofer.'
            : 'Termina tu viaje antes de pasarte a modo chofer.',
        };
      }

      // Terminado, inexistente, abandonado o ajeno: el puntero ya no sirve.
      await rideSessionStore.clearActiveRide();
    }
  }

  let conGpsVivo = false;
  try {
    conGpsVivo = Platform.OS !== 'web' && locationService.modoDeRastreoActual() !== null;
  } catch {}
  // En la WEB no hay vigilante de GPS que mirar. Sin esto, el paso de abajo le
  // apagaba la presencia al chofer web por detrás mientras su mapa seguía
  // diciendo "En línea".
  //
  // V35/V55 — la prueba NO es la intención guardada: sobrevive a recargar la
  // pestaña, el mapa web arranca en "Conectarse" y se bloqueaba el cambio
  // pidiendo un "Desconectarse" que no estaba en pantalla. Cuenta solo lo que
  // se encendió en ESTA carga (el mapa llama a `setDriverOnline(true)` al
  // conectar y a `(false)` al desconectar), y para cualquier modo que conduce
  // —también el repartidor, que tiene el mismo botón—. Una intención vieja
  // sin conexión real pasa y `apagarChoferSiEstabaEnLinea` limpia su resto.
  if (!conGpsVivo && Platform.OS === 'web') {
    try {
      const rolActual = String(useAppStore.getState().userRole ?? '');
      conGpsVivo = ROLES_DE_CHOFER.has(rolActual) && rideSessionStore.enLineaEnEstaCarga();
    } catch {}
  }
  if (conGpsVivo) {
    return {
      ok: false,
      titulo: 'Primero desconéctate',
      mensaje: 'Estás en línea como chofer. Ve al mapa, toca "Desconectarse" y después cambia de modo. Así ningún pasajero te manda un viaje que no vas a ver.',
    };
  }

  await apagarChoferSiEstabaEnLinea(uid);
  return { ok: true };
}

// MIGRADO DE RAILWAY → FIREBASE CLOUD FUNCTIONS.
// El backend de Railway fue borrado (404 en todas sus rutas) y este
// intercambio de token es el que da la sesión NATIVA. Sin él, Firestore y
// Storage deniegan todo y la app se ve en blanco — la causa del rechazo
// "Your app does not open or load" de Google Play.
// La función `exchangeToken` (functions/src/authExchange.ts) hace lo mismo
// y no cuesta un servicio aparte.
const EXCHANGE_TOKEN_URL =
  'https://us-central1-gosafely-3e0e7.cloudfunctions.net/exchangeToken';

/**
 * Pide al backend un custom token de Firebase con el idToken REST que ya
 * tenemos en mano, y firma al SDK NATIVO con ese custom token.
 *
 * Por qué: el custom token NO requiere Play Integrity (es directo del
 * service account del backend), así que funciona aunque el operador móvil
 * esté bloqueando identitytoolkit.googleapis.com. Después de esto,
 * `nativoActual()` está poblado y Firestore/Storage/Functions
 * nativos tienen sesión real → cero `permission-denied`.
 *
 * Fire-and-forget desde el punto de vista del flujo principal: si esto
 * falla, el user igual quedó logueado vía REST (lo único que perdemos es
 * la sesión nativa, que se reintenta en próximas operaciones).
 */
async function exchangeTokenAndSignInNative(idToken: string): Promise<void> {
  // H32 — si mientras esperamos al servidor se cerró la sesión, este canje ya
  // no le pertenece a nadie: firmar el SDK nativo ahora resucitaría la sesión.
  const generacion = generacionSesion;
  try {
    // fetchWithRetry para que un blip de red residencial no nos deje sin
    // sesión nativa. Sin sesión nativa, las queries Firestore caen al
    // permission-denied silencioso — peor UX que el extra de 3-7s de
    // backoff en el peor caso.
    const resp = await fetchWithRetry(EXCHANGE_TOKEN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      perAttemptTimeoutMs: 12_000,
    });
    if (!resp.ok) {
      console.warn('[authBridge] exchange-token respondió', resp.status);
      return;
    }
    const data = await resp.json();
    if (!data?.success || !data?.customToken) {
      console.warn('[authBridge] exchange-token sin customToken');
      return;
    }
    if (generacion !== generacionSesion) {
      console.warn('[authBridge] canje descartado: la sesión se cerró mientras llegaba');
      return;
    }
    await nativeSignInWithCustomToken(getNativeAuth(), data.customToken);
    console.warn('[authBridge] ✅ native session establecida vía custom token');
  } catch (e: any) {
    console.warn('[authBridge] exchange-token / signInWithCustomToken falló:', e?.code, e?.message);
  }
}

// Timeout cortísimo. Sabemos por logcat que el SDK nativo se cuelga en
// Play Integrity y nunca devuelve. Antes esperábamos 8 s — la UX era
// pésima ("tarda mucho para responder"). Ahora damos 2 s de gracia por si
// algún día Play Integrity responde, pero cualquier caso real se va a REST
// en 2 segundos — el endpoint REST responde en <1 s, total ~3 s end-to-end.
const NATIVE_TIMEOUT_MS = 2000;
const STORAGE_KEY = '@gosafely:restAuth';

// Cache en memoria + AsyncStorage. Es el "currentUser" cuando el SDK
// nativo no pudo crear el usuario y caímos al REST.
let _cached: RestAuthUser | null = null;

export const restAuthCache = {
  get(): RestAuthUser | null {
    return _cached;
  },
  async hydrate(): Promise<void> {
    try {
      const raw = await AsyncStorage.getItem(STORAGE_KEY);
      if (raw) _cached = JSON.parse(raw);
    } catch {
      _cached = null;
    }
  },
  async set(user: RestAuthUser | null): Promise<void> {
    _cached = user;
    if (user) {
      await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    } else {
      await AsyncStorage.removeItem(STORAGE_KEY);
    }
  },
};

/** Forma común que devuelven todas las operaciones del bridge. */
export type BridgeUser = {
  uid: string;
  email: string | null;
  emailVerified: boolean;
  /** 'native' si el SDK nativo respondió; 'rest' si fue el fallback. */
  source: 'native' | 'rest';
};

/**
 * Devuelve el usuario actual combinando SDK nativo + cache REST.
 *
 * FIX re-verificación al reabrir — Antes preferíamos nativo y devolvíamos
 * `native.emailVerified` directo. Pero el `currentUser` nativo es un
 * snapshot del momento del signInWithCustomToken: cuando ese se disparó
 * (justo después del signup REST), el correo NO estaba verificado todavía.
 * Después el user verifica vía REST cache → REST.emailVerified pasa a true,
 * pero native.emailVerified queda en false hasta el próximo `reload()`.
 *
 * Al reabrir la app, el observer nativo despierta con ese snapshot stale
 * y el gate de email mandaba al user a /verify-email otra vez.
 *
 * Fix: si CUALQUIERA de los dos canales reporta verificado, tomamos
 * verificado. emailVerified solo puede transitar false → true en Firebase,
 * nunca al revés, así que esto es seguro y elimina el falso re-prompt.
 */
/**
 * El `currentUser` del SDK NATIVO, o `null` en web.
 *
 * En web no existe la app de `@react-native-firebase`, así que
 * `getNativeAuth()` lanza "No Firebase App '[DEFAULT]' has been created". Ese
 * error, disparado desde el observador de sesión al montar el layout raíz,
 * dejaba la web ENTERA en pantalla en blanco.
 *
 * Devolver `null` no es un parche: en web el canal nativo sencillamente no
 * existe, y todos los llamadores ya tienen la rama REST justo debajo, que es
 * la correcta ahí.
 */
function nativoActual(): any | null {
  if (Platform.OS === 'web') return null;
  return getNativeAuth().currentUser;
}

export function getCurrentBridgeUser(): BridgeUser | null {
  const native = nativoActual();
  const rest = restAuthCache.get();

  if (!native && !rest) return null;

  if (native) {
    return {
      uid: native.uid,
      email: native.email,
      emailVerified: native.emailVerified || !!rest?.emailVerified,
      source: 'native',
    };
  }
  return {
    uid: rest!.uid,
    email: rest!.email,
    emailVerified: rest!.emailVerified,
    source: 'rest',
  };
}

/** Race entre la promesa nativa y un timeout. Resuelve a 'TIMEOUT' o al user. */
function raceNative<T>(p: Promise<T>): Promise<T | 'TIMEOUT'> {
  return Promise.race([
    p,
    new Promise<'TIMEOUT'>((resolve) =>
      setTimeout(() => resolve('TIMEOUT'), NATIVE_TIMEOUT_MS)
    ),
  ]);
}

export const authBridge = {
  /** Registro: nativo con timeout corto (NATIVE_TIMEOUT_MS); si timeout → REST + persistir. */
  async signUp(email: string, password: string): Promise<BridgeUser> {
    const nativeAuth = getNativeAuth();
    try {
      const result = await raceNative(nativeCreate(nativeAuth, email, password));
      if (result !== 'TIMEOUT') {
        // Nativo respondió. Disparar verificación nativa, ignorar fallo silencioso.
        try { await nativeSendVerification(result.user); } catch (e) {
          console.warn('[authBridge] native sendVerification falló:', e);
        }
        return {
          uid: result.user.uid,
          email: result.user.email,
          emailVerified: result.user.emailVerified,
          source: 'native',
        };
      }
      console.warn(`[authBridge] SDK nativo no respondió en ${NATIVE_TIMEOUT_MS}ms → fallback REST`);
    } catch (nativeErr: any) {
      // Si nativo rechaza con error fuerte (email-already-in-use, weak-password),
      // NO caemos al REST — propagamos para que la UI muestre el error correcto.
      const code = String(nativeErr?.code ?? '');
      if (
        code.includes('email-already-in-use') ||
        code.includes('invalid-email') ||
        code.includes('weak-password')
      ) {
        throw nativeErr;
      }
      console.warn('[authBridge] nativo falló con error tolerable, intentando REST:', code);
    }

    // Fallback REST.
    const restUser = await authRest.signUp(email, password);
    await restAuthCache.set(restUser);
    // Correo de verificación fire-and-forget — no bloquea el signup.
    authRest.sendEmailVerification(restUser.idToken).catch((e) => {
      console.warn('[authBridge] REST sendEmailVerification falló (background):', e);
    });
    // Exchange para establecer sesión nativa — necesario para que Firestore
    // queries (crear viaje, etc.) tengan auth. Fire-and-forget también: si
    // falla, el user igual está logueado vía REST.
    exchangeTokenAndSignInNative(restUser.idToken);
    return {
      uid: restUser.uid,
      email: restUser.email,
      emailVerified: restUser.emailVerified,
      source: 'rest',
    };
  },

  /** Login: mismo patrón. */
  async signIn(email: string, password: string): Promise<BridgeUser> {
    const nativeAuth = getNativeAuth();
    try {
      const result = await raceNative(nativeSignIn(nativeAuth, email, password));
      if (result !== 'TIMEOUT') {
        // Si llegó por nativo, limpiamos cualquier cache REST viejo.
        await restAuthCache.set(null);
        return {
          uid: result.user.uid,
          email: result.user.email,
          emailVerified: result.user.emailVerified,
          source: 'native',
        };
      }
      console.warn(`[authBridge] SDK nativo no respondió en ${NATIVE_TIMEOUT_MS}ms → fallback REST`);
    } catch (nativeErr: any) {
      const code = String(nativeErr?.code ?? '');
      if (
        code.includes('wrong-password') ||
        code.includes('user-not-found') ||
        code.includes('invalid-credential') ||
        code.includes('user-disabled') ||
        code.includes('too-many-requests')
      ) {
        throw nativeErr;
      }
      console.warn('[authBridge] nativo falló con error tolerable, intentando REST:', code);
    }

    const restUser = await authRest.signIn(email, password);
    await restAuthCache.set(restUser);
    // Exchange para sesión nativa — necesario para que Firestore funcione.
    exchangeTokenAndSignInNative(restUser.idToken);
    return {
      uid: restUser.uid,
      email: restUser.email,
      emailVerified: restUser.emailVerified,
      source: 'rest',
    };
  },

  /**
   * FIX BUG #2 (causa raíz #1) — Establece (o reintenta) la sesión NATIVA a
   * partir de un idToken. Necesario para que las queries de Firestore (crear
   * viaje, ofertar, etc.) tengan `request.auth`. El path de login con Google en
   * auth.tsx obtenía el token pero NUNCA disparaba el exchange → los usuarios de
   * Google quedaban sin `nativoActual()` → permission-denied
   * silencioso en TODA operación de viaje. Fire-and-forget con retry interno
   * (fetchWithRetry), igual que signIn/signUp.
   */
  ensureNativeSession(idToken: string): void {
    if (!idToken) return;
    exchangeTokenAndSignInNative(idToken);
  },

  /** Reenviar correo de verificación al user actual (native o REST). */
  async sendVerification(): Promise<void> {
    const native = nativoActual();
    if (native) {
      await nativeSendVerification(native);
      return;
    }
    const rest = restAuthCache.get();
    if (!rest) throw new Error('No hay usuario activo para reenviar verificación.');
    await authRest.sendEmailVerification(rest.idToken);
  },

  /**
   * Refresca el estado del user (lee emailVerified actualizado).
   * Devuelve true si el correo fue verificado, false si sigue pendiente.
   *
   * AUDIT FIX A-3 — Antes esta función llamaba a `authRest.lookup(rest.idToken)`
   * directamente con el idToken cacheado. Después de 1 hora el token expiraba
   * y lookup fallaba con auth/invalid-id-token, dejando al user atrapado en
   * /verify-email sin poder confirmar la verificación.
   *
   * Ahora `getIdToken()` interno ya rota tokens REST automáticamente cuando
   * quedan < 60s de validez, así que el lookup recibe siempre un token vivo.
   */
  async reload(): Promise<boolean> {
    const native = nativoActual();
    if (native) {
      try { await native.reload(); } catch {}
      return !!nativoActual()?.emailVerified;
    }
    if (!restAuthCache.get()) return false;
    const generacion = generacionSesion;
    try {
      const token = await this.getIdToken();
      if (!token) return false;
      const info = await authRest.lookup(token);
      const current = restAuthCache.get();
      // H32 — `current` puede ser null tras un cierre, pero se mira la
      // generación igual por si ya entró OTRA cuenta en el ínterin.
      if (current && generacion === generacionSesion) {
        const updated: RestAuthUser = { ...current, emailVerified: info.emailVerified };
        await restAuthCache.set(updated);
      }
      return info.emailVerified;
    } catch {
      return false;
    }
  },

  /**
   * EL ÚNICO CIERRE DE SESIÓN COMPLETO DE LA APP — H01/H10/H11/H32/H33,
   * auditoría 16-sep-2026. Todo botón de "Salir" tiene que pasar por aquí.
   *
   * Antes había cinco botones de salir y cada uno limpiaba un pedazo distinto:
   * el del restaurante y el del portal web ni borraban la caché REST ni el
   * store en disco, así que el siguiente que abría la app en ese teléfono (o en
   * esa PC) arrancaba con el nombre, la foto, el teléfono y los avisos del
   * anterior. Ninguno borraba el puntero del viaje (otra cuenta heredaba el
   * viaje y la oferta pendiente) ni la notificación fija del viaje.
   *
   * Cada pieza va aislada y con tope de tiempo: si una falla (sin red), las
   * demás siguen y el usuario sale igual. NUNCA lanza.
   *
   * EL ORDEN IMPORTA:
   *  1. El uid se toma del STORE y nunca del puente. El borrado de cuenta
   *     (profile.tsx) vacía el store ANTES de llamar aquí para que no se
   *     escriba nada sobre la cuenta recién borrada: `clearPushToken` y
   *     `setDriverOnline` usan `merge:true` y recrearían los documentos.
   *  2. Lo que escribe en Firestore (presencia del chofer, token de push) va
   *     ANTES de cerrar el SDK nativo: sin sesión, las reglas lo deniegan.
   *  3. La caché REST y el store se vacían ANTES del signOut nativo (H32). El
   *     signOut nativo dispara el observador de `_layout.tsx`, que calcula
   *     `user?.uid ?? bridge?.uid`: si la caché REST seguía llena en ese
   *     instante, devolvía el uid al store, re-registraba el token de push y
   *     hasta volvía a canjear la sesión nativa.
   */
  async signOut(): Promise<void> {
    let uid: string | null = null;
    try { uid = useAppStore.getState().userFirebaseUid; } catch {}

    generacionSesion++;
    cerrandoSesion = true;
    try {
      // (a) Chofer en línea → GPS apagado y presencia en false (H09/H01). Y la
      //     notificación fija del viaje, que es local y no necesita red (H33):
      //     el siguiente usuario del teléfono la veía en la barra de estado.
      // (b) Token de push (FIX A-6): los avisos del usuario anterior no pueden
      //     seguir llegando a este teléfono. En WEB no se borra: la web nunca
      //     registra token, y el que hay en /users/{uid} es el del TELÉFONO de
      //     esa persona — salir del portal le dejaría el móvil mudo.
      // Van en paralelo: las dos esperan a la red y el usuario está esperando.
      await Promise.all([
        apagarChoferSiEstabaEnLinea(uid),
        esperarConTope(notificationService.clearPersistentRideStatus(), 3000, 'quitar la notificación del viaje'),
        uid && Platform.OS !== 'web'
          ? esperarConTope(notificationService.clearPushToken(uid), 8000, 'borrar el token de push')
          : Promise.resolve(),
      ]);

      // (c) Oferta pendiente e intención de estar en línea (H10). El puntero
      //     del viaje se CONSERVA con su uid dueño (V36): si el viaje sigue vivo
      //     y vuelve la misma cuenta, lo ve y no queda sin poder pedir otro; si
      //     entra otra cuenta, `rideSessionStore.read` lo descarta.
      await esperarConTope(rideSessionStore.clearAlCerrarSesion(), 3000, 'borrar el viaje guardado');

      // (d) Caché REST + store, ANTES del signOut nativo (H32, ver arriba).
      try {
        await restAuthCache.set(null);
      } catch (e: any) {
        // Si el disco falla, al menos la memoria queda vacía para esta sesión.
        _cached = null;
        console.warn('[authBridge] no se pudo borrar la caché REST del disco:', e?.message);
      }
      // FIX A-5 — Limpiar TODO el estado de sesión del store (también su copia
      // en disco, `go-safely-storage-v2`: el `persist` la reescribe al cambiar).
      try { useAppStore.getState().resetSession(); } catch {}

      // (e) Cerrar el SDK que tenga la sesión en esta plataforma. En web la
      //     sesión vive en el SDK web (`firebase/auth`): `getNativeAuth()`
      //     lanza ahí, y por eso el portal no podía usar este camino.
      if (Platform.OS === 'web') {
        await esperarConTope(
          (async () => webSignOut(webAuth))(),
          5000,
          'cerrar la sesión web'
        );
      } else {
        await esperarConTope(
          (async () => nativeSignOut(getNativeAuth()))(),
          5000,
          'cerrar la sesión nativa'
        );
      }

      // (f) El código de referido: NO hay nada que olvidar. `referralService`
      //     ya no guarda caché en memoria (`resetCache` es un no-op documentado)
      //     y el código se pide al servidor como el usuario ACTUAL. No se
      //     importa aquí a propósito: en web ese `import()` dinámico reporta un
      //     "Requiring unknown module" al errorReporter aunque funcione.

      // Segunda pasada: si algún eco del observador escribió en el store entre
      // (d) y (e), aquí queda vacío otra vez. Es un `set` de Zustand: gratis.
      try { useAppStore.getState().resetSession(); } catch {}
    } finally {
      cerrandoSesion = false;
    }
  },

  /**
   * Devuelve un Firebase ID token VÁLIDO para usar como
   * `Authorization: Bearer <idToken>` en llamadas al backend.
   *
   * - Native: delega a `currentUser.getIdToken()` (rota automáticamente).
   * - REST: si el cached `idToken` está por expirar (margen 60s), lo
   *   renueva con el refreshToken antes de devolverlo. Sin esto, después
   *   de 1 hora el token cacheado quedaría inválido y cualquier llamada
   *   protegida fallaría con auth/invalid-id-token sin recuperación.
   *
   * Devuelve `null` si no hay user activo en ninguno de los canales.
   */
  async getIdToken(): Promise<string | null> {
    const native = nativoActual();
    if (native) {
      try {
        return await native.getIdToken();
      } catch (e) {
        console.warn('[authBridge] native.getIdToken falló:', e);
        return null;
      }
    }
    const rest = restAuthCache.get();
    if (!rest) return null;
    // Si quedan menos de 60s de validez, refrescar antes de devolver.
    if (Date.now() > rest.expiresAt - 60_000) {
      const generacion = generacionSesion;
      try {
        const refreshed = await authRest.refresh(rest.refreshToken);
        // H32 — si la sesión se cerró durante la renovación, guardar ahora
        // volvería a escribir en disco la caché REST que signOut acaba de
        // borrar, y al próximo arranque el usuario aparecería dentro otra vez.
        if (generacion !== generacionSesion) return null;
        const updated: RestAuthUser = {
          ...rest,
          idToken: refreshed.idToken,
          refreshToken: refreshed.refreshToken,
          expiresAt: refreshed.expiresAt,
        };
        await restAuthCache.set(updated);
        return refreshed.idToken;
      } catch (e) {
        console.warn('[authBridge] REST refresh falló — token expirado sin recuperación:', e);
        return null;
      }
    }
    return rest.idToken;
  },
};
